/**
 * SU-5 (W7, CEO-14): sends the browser's uncaught errors to
 * POST /api/client-errors, read by the super admin in « Erreurs client ».
 *
 * Sources: window `error`, window `unhandledrejection`, and the error
 * boundaries (DebugErrorBoundary calls reportClientError).
 *
 * Restraint, on top of the server's limits (30 a minute per IP, 8 KB):
 *   - never in tests or automated browsers (Vitest, Playwright: MODE 'test',
 *     navigator.webdriver) and not in development unless
 *     VITE_CLIENT_ERRORS_IN_DEV is set;
 *   - deduped: one report per fingerprint (kind + name + message + first
 *     stack line) every 5 minutes; the repeats are counted and sent with the
 *     next report of that error (`count`);
 *   - sampled: a new error is sent with probability SAMPLE_RATE
 *     (VITE_CLIENT_ERRORS_SAMPLE_RATE, default 0.5);
 *   - at most 20 reports per page load; the dedupe memory keeps the 200
 *     most recent fingerprints (REV-W7-F15: a long session never grows it);
 *   - a cross-origin « Script error. » (no error object, no detail: the
 *     browser hides it) is skipped, it says nothing a developer can use;
 *   - the fields the server accepts, and only those (fixed schema), trimmed
 *     so the body stays under 8 KB; the route is the path only.
 * The server removes emails, phone numbers and tokens before storing.
 * Sending never throws and never reports its own failures.
 */
import { API_BASE } from '@/api/api-client';

type Kind = 'error' | 'unhandledrejection' | 'boundary';

const DEDUPE_MS = 5 * 60_000;
const SESSION_CAP = 20;
const SEEN_CAP = 200;
const env = import.meta.env as Record<string, string | boolean | undefined>;
const SAMPLE_RATE = (() => {
  const n = Number(env.VITE_CLIENT_ERRORS_SAMPLE_RATE);
  return Number.isFinite(n) && n >= 0 && n <= 1 && env.VITE_CLIENT_ERRORS_SAMPLE_RATE !== undefined ? n : 0.5;
})();

interface Seen { at: number; pending: number }
const seen = new Map<string, Seen>();
let sent = 0;
let installed = false;

export function reporterEnabled(): boolean {
  if (env.MODE === 'test' || env.VITEST) return false;
  if (typeof navigator !== 'undefined' && navigator.webdriver) return false;
  if (env.DEV && !env.VITE_CLIENT_ERRORS_IN_DEV) return false;
  return typeof window !== 'undefined';
}

function asError(value: unknown): { name: string; message: string; stack: string } {
  if (value instanceof Error) return { name: value.name || 'Error', message: value.message || String(value), stack: value.stack ?? '' };
  if (typeof value === 'string') return { name: 'Error', message: value, stack: '' };
  try {
    return { name: 'NonError', message: JSON.stringify(value)?.slice(0, 1000) ?? String(value), stack: '' };
  } catch {
    return { name: 'NonError', message: String(value), stack: '' };
  }
}

/** The body sent, exported for the tests. */
export function buildReport(kind: Kind, value: unknown, path: string, count = 1) {
  const e = asError(value);
  return {
    kind,
    name: e.name.slice(0, 100),
    message: (e.message || '(no message)').slice(0, 1000),
    stack: e.stack.slice(0, 4000) || undefined,
    route: path.split(/[?#]/)[0]!.slice(0, 300),
    release: typeof env.VITE_RELEASE === 'string' ? env.VITE_RELEASE.slice(0, 64) : undefined,
    count: Math.min(100, Math.max(1, count)),
  };
}

export function fingerprintOf(kind: Kind, value: unknown): string {
  const e = asError(value);
  return `${kind}|${e.name}|${e.message}|${e.stack.split('\n')[1]?.trim() ?? ''}`;
}

/**
 * Decides whether to send now. Returns the count to send (repeats included)
 * or 0 to skip. Exported for the tests, with injectable clock and random.
 */
export function admit(fp: string, now: number, random: number): number {
  const s = seen.get(fp);
  if (s && now - s.at < DEDUPE_MS) {
    s.pending += 1;
    return 0;
  }
  if (sent >= SESSION_CAP) return 0;
  const count = 1 + (s?.pending ?? 0);
  // Re-inserted last, so the map's first key is always the oldest one.
  seen.delete(fp);
  seen.set(fp, { at: now, pending: 0 });
  while (seen.size > SEEN_CAP) seen.delete(seen.keys().next().value as string);
  // Sampling decides once per fingerprint window: a skipped error is not retried for 5 minutes.
  if (random >= SAMPLE_RATE) return 0;
  sent += 1;
  return count;
}

/** For the tests. */
export function seenSize(): number {
  return seen.size;
}

/**
 * Whether a window `error` event is worth a report. Not when there is nothing
 * to report (a resource that failed to load: no error, no message), nor for
 * the browser's opaque cross-origin « Script error. » without an error object.
 */
export function shouldReportErrorEvent(event: Pick<ErrorEvent, 'error' | 'message'>): boolean {
  if (event.error) return true;
  if (!event.message) return false;
  return !/^Script error\.?$/i.test(event.message.trim());
}

/** For the tests. */
export function resetReporter(): void {
  seen.clear();
  sent = 0;
}

function send(body: unknown): void {
  try {
    const json = JSON.stringify(body);
    if (json.length > 8000) return;
    const url = `${API_BASE}/client-errors`;
    if (typeof navigator !== 'undefined' && typeof navigator.sendBeacon === 'function') {
      if (navigator.sendBeacon(url, new Blob([json], { type: 'text/plain;charset=UTF-8' }))) return;
    }
    void fetch(url, { method: 'POST', body: json, headers: { 'content-type': 'application/json' }, credentials: 'omit', keepalive: true }).catch(() => {});
  } catch {
    /* never report the reporter */
  }
}

export function reportClientError(value: unknown, kind: Kind = 'boundary'): void {
  if (!reporterEnabled()) return;
  try {
    const count = admit(fingerprintOf(kind, value), Date.now(), Math.random());
    if (count > 0) send(buildReport(kind, value, window.location.pathname, count));
  } catch {
    /* never report the reporter */
  }
}

export function installClientErrorReporter(): void {
  if (installed || !reporterEnabled()) return;
  installed = true;
  window.addEventListener('error', (event: ErrorEvent) => {
    if (!shouldReportErrorEvent(event)) return;
    reportClientError(event.error ?? event.message, 'error');
  });
  window.addEventListener('unhandledrejection', (event: PromiseRejectionEvent) => {
    reportClientError(event.reason, 'unhandledrejection');
  });
}
